import { useMemo } from "react";
import { useAppSelector, selectors } from "store";
import { useChartTimeline, percentToDisplayTime, } from "ui/components/elements/chart/chart-timeline";
/**
 * Linear interpolation of `values` (parallel to `times`) at `apiTime`.
 *
 * Mirrors interpolateVacuumLevel: clamps to the first/last sample when
 * `apiTime` falls outside the range. Returns 0 when there is no data.
 */
export var interpolateByTime = function (times, values, apiTime) {
    var _a, _b, _c, _d;
    if (!times.length || !values.length)
        return 0;
    var idx = times.findIndex(function (t) { return t >= apiTime; });
    if (idx === -1)
        return (_a = values[values.length - 1]) !== null && _a !== void 0 ? _a : 0;
    if (idx === 0)
        return (_b = values[0]) !== null && _b !== void 0 ? _b : 0;
    var t0 = times[idx - 1];
    var t1 = times[idx];
    var v0 = (_c = values[idx - 1]) !== null && _c !== void 0 ? _c : 0;
    var v1 = (_d = values[idx]) !== null && _d !== void 0 ? _d : 0;
    var alpha = t1 === t0 ? 0 : (apiTime - t0) / (t1 - t0);
    return v0 + alpha * (v1 - v0);
};
/**
 * Returns the vertical & horizontal lifting force at the current play
 * position, or undefined when the time-series data is not available.
 *
 * With several lifted boxes the value follows the weakest box's curve — the
 * one the Lifting forces box headlines — because the system-total curve has
 * nothing to be compared against there (the required force is per box).
 * Otherwise it is the system total.
 *
 * Uses the same percent→display-time mapping as the graph PlayLine (see
 * use-current-vacuum-level) so the sampled value lines up exactly with the
 * rendered play head.
 */
export var useCurrentLiftingForce = function () {
    var playLocationPercent = useAppSelector(selectors.selectPlayLocationPercent);
    var performanceData = useAppSelector(selectors.selectActivePerformanceData);
    var weakestBox = useAppSelector(selectors.selectWeakestBoxLiftingForce);
    var timeline = useChartTimeline();
    return useMemo(function () {
        if (!performanceData || !timeline)
            return undefined;
        // The per-box series are per-cup sums sampled on the same time axis as
        // the system totals, so both variants interpolate against `times`.
        var times = performanceData.vacuumLevelTimeData;
        var vertical = weakestBox
            ? weakestBox.box.verticalVsTime
            : performanceData.liftingForceVerticalVsTime;
        var horizontal = weakestBox
            ? weakestBox.box.horizontalVsTime
            : performanceData.liftingForceHorizontalVsTime;
        if (!times.length || !(vertical === null || vertical === void 0 ? void 0 : vertical.length) || !(horizontal === null || horizontal === void 0 ? void 0 : horizontal.length))
            return undefined;
        var apiTime = percentToDisplayTime(playLocationPercent, timeline);
        return {
            vertical: interpolateByTime(times, vertical, apiTime),
            horizontal: interpolateByTime(times, horizontal, apiTime),
        };
    }, [performanceData, weakestBox, timeline, playLocationPercent]);
};
